import { useCallback, useState, useEffect, useRef, DragEvent, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  Edge,
  Node,
  BackgroundVariant,
  ReactFlowProvider,
  NodeTypes,
  EdgeProps,
  getBezierPath,
  EdgeLabelRenderer,
  BaseEdge,
} from 'reactflow';
import 'reactflow/dist/style.css';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { TriggerNode } from '../components/flow-nodes/TriggerNode';
import { TextNode } from '../components/flow-nodes/TextNode';
import { ImageNode } from '../components/flow-nodes/ImageNode';
import { VideoNode } from '../components/flow-nodes/VideoNode';
import { AudioNode } from '../components/flow-nodes/AudioNode';
import { DocumentNode } from '../components/flow-nodes/DocumentNode';
import { ActionNode } from '../components/flow-nodes/ActionNode';
import { ConditionNode } from '../components/flow-nodes/ConditionNode';
import { DelayNode } from '../components/flow-nodes/DelayNode';
import { HttpRestNode } from '../components/flow-nodes/HttpRestNode';
import { StopNode } from '../components/flow-nodes/StopNode';
import { IntegrationPerfexNode } from '../components/flow-nodes/IntegrationPerfexNode';
import { IntegrationChatwootNode } from '../components/flow-nodes/IntegrationChatwootNode';
import { SaveVariableNode } from '../components/flow-nodes/SaveVariableNode';
import { NodeConfigSidebar } from '../components/flow-nodes/NodeConfigSidebar';
import { FlowPreviewModal } from '../components/FlowPreviewModal';
import { useSidebar } from '../contexts/SidebarContext';
import { interactiveCampaignApi, InteractiveCampaign, Connection as ApiConnection } from '../services/interactiveCampaignApi';
import { api } from '../services/api';

// Tipos de nodes disponíveis
const NODE_TYPES_CONFIG = {
  trigger: { label: '⚡ Início', color: '#8ad0f3', description: 'Inicia' },
  text: { label: '📝 Texto', color: '#3ddc97', description: 'Mensagem' },
  image: { label: '🖼️ Imagem', color: '#60a5fa', description: 'Foto' },
  video: { label: '🎬 Vídeo', color: '#a78bfa', description: 'Vídeo' },
  audio: { label: '🎵 Áudio', color: '#f59e0b', description: 'Áudio' },
  document: { label: '📄 Arquivo', color: '#ec4899', description: 'Doc' },
  condition: { label: '❓ Condição', color: '#ffd700', description: 'Condição' },
  saveVariable: { label: '💾 Variável', color: '#10b981', description: 'Salvar variáveis' },
  delay: { label: '⏱️ Delay', color: '#ff7a7a', description: 'Espera' },
  httprest: { label: '🌐 Http Request', color: '#f59e0b', description: 'API' },
  stop: { label: '🛑 Stop', color: '#233e4f', description: 'Fim' },
};

// Tipos de nodes de integração (exibidos condicionalmente)
const INTEGRATION_NODES_CONFIG = {
  integration_perfex: { label: '🔧 Perfex CRM', color: '#9333ea', description: 'Integração' },
  integration_chatwoot: { label: '💬 Chatwoot', color: '#1f93ff', description: 'Integração' },
};

// Edge customizada com botão de deletar
function CustomEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  data,
}: EdgeProps) {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const onEdgeClick = () => {
    if (data?.onDelete) {
      data.onDelete(id);
    }
  };

  return (
    <>
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            fontSize: 12,
            pointerEvents: 'all',
          }}
          className="nodrag nopan"
        >
          <button
            className="w-5 h-5 bg-white hover:bg-gray-50 border border-gray-300 hover:border-red-400 text-gray-400 hover:text-red-500 rounded-full flex items-center justify-center text-sm shadow-sm transition-all hover:scale-110"
            onClick={onEdgeClick}
            title="Deletar conexão"
          >
            ×
          </button>
        </div>
      </EdgeLabelRenderer>
    </>
  );
}

function FlowBuilderPageInner() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { setIsOpen } = useSidebar();
  const isBotFlow = location.pathname.startsWith('/campanhas/bot/');
  const [nodes, setNodes, onNodesChange] = useNodesState([]);

  // Recolher menu lateral ao entrar no flow builder; reabrir ao sair
  useEffect(() => {
    setIsOpen(false);
    return () => {
      setIsOpen(true);
    };
  }, [setIsOpen]);

  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [campaign, setCampaign] = useState<InteractiveCampaign | null>(null);
  const [connections, setConnections] = useState<ApiConnection[]>([]);
  const [categories, setCategories] = useState<Array<{ id: string; nome: string; _count?: { contacts: number } }>>([]);
  const [withoutCategoryCount, setWithoutCategoryCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [integrationSettings, setIntegrationSettings] = useState<{
    hasPerfex: boolean;
    hasChatwoot: boolean;
  }>({ hasPerfex: false, hasChatwoot: false });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Custom node types
  const nodeTypes: NodeTypes = useMemo(
    () => ({
      trigger: TriggerNode,
      text: TextNode,
      image: ImageNode,
      video: VideoNode,
      audio: AudioNode,
      document: DocumentNode,
      action: ActionNode,
      condition: ConditionNode,
      saveVariable: SaveVariableNode,
      delay: DelayNode,
      httprest: HttpRestNode,
      stop: StopNode,
      integration_perfex: IntegrationPerfexNode,
      integration_chatwoot: IntegrationChatwootNode,
    }),
    []
  );

  // Custom edge types
  const edgeTypes = useMemo(
    () => ({
      default: CustomEdge,
    }),
    []
  );

  const handleDeleteNode = useCallback((nodeId: string) => {
    setNodes((nds) => nds.filter((node) => node.id !== nodeId));
    setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
  }, [setNodes, setEdges]);

  const handleDeleteEdge = useCallback((edgeId: string) => {
    setEdges((eds) => eds.filter((edge) => edge.id !== edgeId));
  }, [setEdges]);

  useEffect(() => {
    if (id) {
      loadCampaign(id);
      loadConnections();
      loadCategories();
      loadContactStats();
      loadIntegrationSettings();
    }
  }, [id]);

  const loadCampaign = async (campaignId: string) => {
    try {
      setLoading(true);
      const data = await interactiveCampaignApi.getCampaign(campaignId);
      setCampaign(data);

      if (data.graph?.nodes) {
        // Adicionar função onDelete e campaignType para cada node carregado
        const nodesWithDelete = data.graph.nodes.map((node: Node) => ({
          ...node,
          hidden: node.data?.nodeType === 'ai',
          data: {
            ...node.data,
            campaignType: data.type, // Adicionar tipo da campanha
            onDelete: () => handleDeleteNode(node.id),
          },
        }));
        setNodes(nodesWithDelete);

        // Adicionar função onDelete para cada edge carregada
        const edgesWithDelete = (data.graph.edges || []).map((edge: Edge) => ({
          ...edge,
          data: {
            ...edge.data,
            onDelete: handleDeleteEdge,
          },
        }));
        setEdges(edgesWithDelete);
      }
    } catch (error: any) {
      toast.error(`Erro ao carregar campanha: ${error.message}`);
      navigate(isBotFlow ? '/campanhas/bot' : '/campanhas/interativa');
    } finally {
      setLoading(false);
    }
  };

  const loadConnections = async () => {
    try {
      const data = await interactiveCampaignApi.getConnections();
      setConnections(data);
    } catch (error: any) {
      console.error('Error loading connections:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await api.getAllCategories();
      setCategories(data);
    } catch (error: any) {
      console.error('Error loading categories:', error);
      // Fallback para método paginado se getAllCategories falhar
      try {
        const response = await api.getCategories({ pageSize: 1000 });
        setCategories(response.categories);
      } catch (fallbackError: any) {
        console.error('Error loading categories (fallback):', fallbackError);
      }
    }
  };

  const loadContactStats = async () => {
    try {
      const stats = await api.getContactStats();
      setWithoutCategoryCount(stats.withoutCategory);
    } catch {
      setWithoutCategoryCount(null);
    }
  };

  const loadIntegrationSettings = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const response = await fetch('/api/settings', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const settings = await response.json();
        setIntegrationSettings({
          hasPerfex: !!(settings.perfexUrl && settings.perfexToken),
          hasChatwoot: !!(settings.chatwootUrl && settings.chatwootAccountId && settings.chatwootApiToken),
        });
      }
    } catch (error: any) {
      console.error('Error loading integration settings:', error);
    }
  };

  const onConnect = useCallback(
    (params: Edge | Connection) => {
      const newEdge = {
        ...params,
        data: { onDelete: handleDeleteEdge },
      };
      setEdges((eds) => addEdge(newEdge, eds));
    },
    [setEdges, handleDeleteEdge]
  );

  const handleSave = async () => {
    if (!campaign) return;

    setSaving(true);

    try {
      const graph = { nodes, edges };

      // Extrair scheduledDate e connectionId do nó trigger
      const triggerNode = nodes.find(n => n.data.nodeType === 'trigger');
      const triggerConfig = triggerNode?.data?.config;

      let scheduledDateTime = null;
      if (triggerConfig?.scheduleType === 'scheduled' && triggerConfig?.scheduledDate && triggerConfig?.scheduledTime) {
        scheduledDateTime = new Date(`${triggerConfig.scheduledDate}T${triggerConfig.scheduledTime}:00`);
      }

      // Extrair connectionId do trigger (primeiro item do array connections)
      const connectionId = triggerConfig?.connections?.[0] || undefined;

      await interactiveCampaignApi.updateCampaign(campaign.id, {
        graph,
        scheduledDate: scheduledDateTime || undefined,
        connectionId,
        allowGroups: false, // Não liberar grupos por enquanto
      });

      toast.success('Fluxo salvo com sucesso!');
    } catch (error: any) {
      toast.error(`Erro ao salvar: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!campaign) return;

    // Validar se há pelo menos um nó trigger
    const triggerNode = nodes.find(n => n.data.nodeType === 'trigger');
    if (!triggerNode) {
      toast.error('Adicione um nó Início ao fluxo antes de publicar');
      return;
    }

    const triggerConfig = triggerNode.data.config;

    // Validar configurações do trigger
    if (!triggerConfig?.connections?.length) {
      toast.error('Configure pelo menos uma conexão WhatsApp no Início');
      return;
    }

    // Validar categorias apenas para OUTBOUND (campanhas)
    // IMPORTANTE: Bots INBOUND nunca precisam de categorias
    const isOutbound = campaign?.type === 'OUTBOUND';
    if (isOutbound) {
      if (!triggerConfig?.categories?.length) {
        toast.error('Configure pelo menos uma categoria de contatos no Início');
        return;
      }
    }

    // Validar agendamento apenas para OUTBOUND
    let scheduledDateTime = null;
    if (campaign?.type === 'OUTBOUND' && triggerConfig?.scheduleType === 'scheduled') {
      if (!triggerConfig?.scheduledDate || !triggerConfig?.scheduledTime) {
        toast.error('Por favor, defina a data e hora do agendamento no Início');
        return;
      }
      scheduledDateTime = new Date(`${triggerConfig.scheduledDate}T${triggerConfig.scheduledTime}:00`);
    }

    const message = scheduledDateTime
      ? `Deseja publicar a ${campaign?.type === 'INBOUND' ? 'bot' : 'campanha'} "${campaign.name}"?\nEla será iniciada em: ${scheduledDateTime.toLocaleString('pt-BR')}`
      : `Deseja publicar a ${campaign?.type === 'INBOUND' ? 'bot' : 'campanha'} "${campaign.name}"? ${campaign?.type === 'INBOUND' ? 'O bot começará a escutar mensagens imediatamente.' : 'Ela começará a processar mensagens imediatamente.'}`;

    if (!confirm(message)) {
      return;
    }

    try {
      // Salvar antes de publicar
      await handleSave();

      await interactiveCampaignApi.publishCampaign(campaign.id, scheduledDateTime || undefined);

      if (scheduledDateTime) {
        toast.success(`${campaign?.type === 'INBOUND' ? 'Bot' : 'Campanha'} agendada para ${scheduledDateTime.toLocaleString('pt-BR')}!`);
      } else {
        toast.success(`${campaign?.type === 'INBOUND' ? 'Bot' : 'Campanha'} publicada!`);
      }

      // Recarregar
      await loadCampaign(campaign.id);
    } catch (error: any) {
      toast.error(`Erro ao publicar: ${error.message}`);
    }
  };

  const handleExportFlow = useCallback(() => {
    try {
      // Remover onDelete e outros não-serializáveis dos nodes
      const nodesToExport = nodes.map((node) => {
        const { onDelete, ...restData } = node.data || {};
        return {
          ...node,
          data: restData,
        };
      });
      // Remover onDelete das edges
      const edgesToExport = edges.map((edge) => {
        const { onDelete, ...restData } = (edge.data || {}) as Record<string, unknown>;
        return {
          ...edge,
          data: Object.keys(restData).length ? restData : undefined,
        };
      });

      const payload = {
        version: 1,
        exportedAt: new Date().toISOString(),
        campaignName: campaign?.name || 'Fluxo exportado',
        nodes: nodesToExport,
        edges: edgesToExport,
      };

      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fluxo-${(campaign?.name || 'exportado').replace(/\s+/g, '-').toLowerCase()}-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Fluxo exportado com sucesso!');
    } catch (error: any) {
      toast.error(`Erro ao exportar: ${error.message}`);
    }
  }, [nodes, edges, campaign?.name]);

  const handleImportFlow = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const raw = e.target?.result as string;
          const data = JSON.parse(raw) as {
            version?: number;
            nodes?: Node[];
            edges?: Edge[];
            campaignName?: string;
          };

          if (!Array.isArray(data.nodes) || !Array.isArray(data.edges)) {
            toast.error('Arquivo inválido: deve conter "nodes" e "edges".');
            return;
          }

          // Adicionar onDelete nos nodes e edges
          const nodesWithDelete = data.nodes.map((node: Node) => ({
            ...node,
            data: {
              ...node.data,
              campaignType: campaign?.type,
              onDelete: () => handleDeleteNode(node.id),
            },
          }));
          const edgesWithDelete = data.edges.map((edge: Edge) => ({
            ...edge,
            data: {
              ...edge.data,
              onDelete: handleDeleteEdge,
            },
          }));

          setNodes(nodesWithDelete);
          setEdges(edgesWithDelete);
          toast.success(`Fluxo importado! ${data.nodes.length} nós, ${data.edges.length} conexões.`);
        } catch (err: any) {
          toast.error(`Erro ao importar: ${err.message || 'arquivo inválido'}`);
        } finally {
          event.target.value = '';
        }
      };
      reader.readAsText(file);
    },
    [campaign?.type, handleDeleteNode, handleDeleteEdge, setNodes, setEdges]
  );

  const onDragStart = (event: DragEvent, nodeType: string) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = useCallback((event: DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');

      if (typeof type === 'undefined' || !type) {
        return;
      }

      if (!reactFlowInstance) {
        return;
      }

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const nodeId = `${type}-${Date.now()}`;

      // Buscar configuração do nó (pode estar em NODE_TYPES_CONFIG ou INTEGRATION_NODES_CONFIG)
      const nodeConfig = NODE_TYPES_CONFIG[type as keyof typeof NODE_TYPES_CONFIG] ||
                         INTEGRATION_NODES_CONFIG[type as keyof typeof INTEGRATION_NODES_CONFIG];

      const newNode: Node = {
        id: nodeId,
        type: type, // Use o tipo customizado
        position,
        data: {
          label: nodeConfig?.label || type,
          nodeType: type,
          campaignType: campaign?.type, // Passar tipo da campanha
          config: {}, // Configuração vazia inicialmente
          onDelete: () => handleDeleteNode(nodeId),
        },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, setNodes, handleDeleteNode]
  );

  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    // Não abrir config para node do tipo stop
    if (node.data.nodeType !== 'stop') {
      setSelectedNode(node);
    }
  }, []);

  const handleNodeConfigSave = (nodeId: string, config: any) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          return {
            ...node,
            data: {
              ...node.data,
              config,
            },
          };
        }
        return node;
      })
    );
  };

  const handleBack = () => {
    navigate(isBotFlow ? '/campanhas/bot' : '/campanhas/interativa');
  };

  const handleNameClick = () => {
    if (campaign) {
      setEditedName(campaign.name);
      setIsEditingName(true);
    }
  };

  const handleNameSave = async () => {
    if (!campaign || !id || !editedName.trim()) {
      setIsEditingName(false);
      return;
    }

    try {
      await interactiveCampaignApi.updateCampaign(id, { name: editedName.trim() });
      setCampaign({ ...campaign, name: editedName.trim() });
      setIsEditingName(false);
      toast.success('Nome atualizado com sucesso!');
    } catch (error: any) {
      toast.error(`Erro ao atualizar nome: ${error.message}`);
    }
  };

  const handleNameCancel = () => {
    setIsEditingName(false);
    setEditedName('');
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleNameSave();
    } else if (e.key === 'Escape') {
      handleNameCancel();
    }
  };

  // Calcular subtitle: conexão selecionada no nó de início (trigger)
  const subtitle = useMemo(() => {
    // 1) campaign.connection (vindo do backend)
    if (campaign?.connection?.instanceName) {
      return `${campaign.connection.instanceName}${campaign.connection.phoneNumber ? ` (${campaign.connection.phoneNumber})` : ''}`;
    }

    // 2) Resolver a partir do trigger: tentar cada connectionId (Connection ou WhatsAppSession)
    const triggerNode = nodes.find(n => n.data?.nodeType === 'trigger');
    const triggerIds = triggerNode?.data?.config?.connections ?? [];
    for (const connectionId of triggerIds) {
      const c = connections.find(x => x.id === connectionId);
      if (c) {
        return `${c.instanceName}${c.phoneNumber ? ` (${c.phoneNumber})` : ''}`;
      }
    }

    if (triggerIds.length > 0) {
      return `${triggerIds.length} conexão(ões) selecionada(s)`;
    }
    return 'Sem conexão configurada';
  }, [campaign?.connection, nodes, connections]);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-ui-bg">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  if (!campaign) {
    return null;
  }

  return (
    <div className="h-screen flex flex-col bg-ui-bg">
      {/* Header: só título e subtítulo, sem botões na topbar */}
      <Header
        title={
          isEditingName ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onKeyDown={handleNameKeyDown}
                onBlur={handleNameSave}
                autoFocus
                className="px-3 py-1 border-2 border-emerald-500 rounded-xl text-xl font-semibold bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-emerald-500/50 dark:text-white"
                style={{ minWidth: '300px' }}
              />
            </div>
          ) : (
            <div
              onClick={handleNameClick}
              className="cursor-pointer hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors flex items-center gap-2 group"
              title="Clique para editar o nome"
            >
              <span>{campaign.name}</span>
              <i className="fas fa-pen text-sm opacity-0 group-hover:opacity-70 transition-opacity" aria-hidden />
            </div>
          )
        }
        subtitle={<span>{subtitle}</span>}
        compactTopbar
      />

      {/* Conteúdo: barra de blocos à esquerda; à direita a barra de botões e o canvas */}
      <div className="flex-1 flex overflow-hidden">
        {/* Barra de blocos (lateral esquerda) — padrão visual do sistema */}
        <aside className="w-72 shrink-0 flex flex-col gap-5 bg-white dark:bg-gray-900/50 border-r border-gray-200 dark:border-gray-700 py-5 px-4 shadow-sm overflow-y-auto">
          <div>
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Blocos de ação
            </h2>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(NODE_TYPES_CONFIG).map(([nodeType, config]) => (
                <div
                  key={nodeType}
                  draggable
                  onDragStart={(event) => onDragStart(event, nodeType)}
                  className="group flex flex-col justify-center rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800/50 py-2 px-3 text-left transition-all duration-200 cursor-grab active:cursor-grabbing hover:border-emerald-400/60 hover:bg-emerald-50/50 dark:hover:bg-gray-700/50 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/30"
                  style={{ borderLeftWidth: 4, borderLeftColor: config.color }}
                >
                  <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">{config.label}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5 flex items-center justify-between gap-2">
                    <span>{config.description}</span>
                    <i className="fas fa-grip-vertical text-gray-300 dark:text-gray-500 group-hover:text-emerald-500/70 text-xs shrink-0" aria-hidden />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Integrações Externas */}
          {(integrationSettings.hasPerfex || integrationSettings.hasChatwoot) && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                Integrações
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {integrationSettings.hasPerfex && (
                  <div
                    draggable
                    onDragStart={(event) => onDragStart(event, 'integration_perfex')}
                    className="group flex flex-col justify-center rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800/50 py-2 px-3 text-left transition-all duration-200 cursor-grab active:cursor-grabbing hover:border-emerald-400/60 hover:bg-emerald-50/50 dark:hover:bg-gray-700/50 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/30"
                    style={{ borderLeftWidth: 4, borderLeftColor: INTEGRATION_NODES_CONFIG.integration_perfex.color }}
                  >
                    <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">{INTEGRATION_NODES_CONFIG.integration_perfex.label}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5 flex items-center justify-between gap-2">
                      <span>{INTEGRATION_NODES_CONFIG.integration_perfex.description}</span>
                      <i className="fas fa-grip-vertical text-gray-300 dark:text-gray-500 group-hover:text-emerald-500/70 text-xs shrink-0" aria-hidden />
                    </div>
                  </div>
                )}
                {integrationSettings.hasChatwoot && (
                  <div
                    draggable
                    onDragStart={(event) => onDragStart(event, 'integration_chatwoot')}
                    className="group flex flex-col justify-center rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800/50 py-2 px-3 text-left transition-all duration-200 cursor-grab active:cursor-grabbing hover:border-emerald-400/60 hover:bg-emerald-50/50 dark:hover:bg-gray-700/50 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/30"
                    style={{ borderLeftWidth: 4, borderLeftColor: INTEGRATION_NODES_CONFIG.integration_chatwoot.color }}
                  >
                    <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">{INTEGRATION_NODES_CONFIG.integration_chatwoot.label}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5 flex items-center justify-between gap-2">
                      <span>{INTEGRATION_NODES_CONFIG.integration_chatwoot.description}</span>
                      <i className="fas fa-grip-vertical text-gray-300 dark:text-gray-500 group-hover:text-emerald-500/70 text-xs shrink-0" aria-hidden />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50/80 dark:bg-gray-800/40 p-4 mt-auto">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Dicas
            </h3>
            <ul className="space-y-2.5 text-xs text-gray-600 dark:text-gray-300">
              <li className="flex items-start gap-2">
                <i className="fas fa-grip-vertical text-emerald-500/80 mt-0.5 shrink-0" aria-hidden />
                <span>Arraste blocos para o canvas</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="fas fa-link text-emerald-500/80 mt-0.5 shrink-0" aria-hidden />
                <span>Conecte blocos arrastando entre eles</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="fas fa-cog text-emerald-500/80 mt-0.5 shrink-0" aria-hidden />
                <span>Clique em um bloco para configurar</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="fas fa-search-plus text-emerald-500/80 mt-0.5 shrink-0" aria-hidden />
                <span>Use scroll para zoom</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="fas fa-cloud-arrow-down text-emerald-500/80 mt-0.5 shrink-0" aria-hidden />
                <span>Salve antes de publicar</span>
              </li>
            </ul>
          </div>
        </aside>

        {/* Área à direita dos blocos: barra de botões + canvas */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Barra de botões (depois da barra de blocos) */}
          <div className="shrink-0 px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/50 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={handleBack}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <i className="fas fa-arrow-left text-sm" aria-hidden />
                Voltar
              </button>

              {campaign.status === 'DRAFT' && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                  📝 Rascunho
                </span>
              )}
              {campaign.status === 'STARTED' && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300">
                  ✅ {campaign.type === 'INBOUND' ? 'Bot ativo' : 'Campanha ativa'}
                </span>
              )}
              {campaign.status === 'PAUSED' && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300">
                  ⏸️ {campaign.type === 'INBOUND' ? 'Bot pausado' : 'Campanha pausada'}
                </span>
              )}
              {campaign.status === 'SCHEDULED' && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                  📅 Agendada
                </span>
              )}
              {campaign.status === 'COMPLETED' && campaign.type === 'OUTBOUND' && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                  ✅ Concluída
                </span>
              )}

              {(campaign.status === 'STARTED' || campaign.status === 'PAUSED') && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      if (campaign.status === 'STARTED') {
                        await interactiveCampaignApi.pauseCampaign(campaign.id);
                        toast.success(`${campaign.type === 'INBOUND' ? 'Bot' : 'Campanha'} pausado com sucesso!`);
                      } else {
                        await interactiveCampaignApi.publishCampaign(campaign.id);
                        toast.success(`${campaign.type === 'INBOUND' ? 'Bot' : 'Campanha'} ativado com sucesso!`);
                      }
                      await loadCampaign(campaign.id);
                    } catch (error: any) {
                      toast.error(`Erro ao ${campaign.status === 'STARTED' ? 'pausar' : 'ativar'}: ${error.message}`);
                    }
                  }}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                    campaign.status === 'STARTED'
                      ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30 border border-amber-200 dark:border-amber-800'
                      : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800'
                  }`}
                  title={campaign.status === 'STARTED' ? 'Pausar' : 'Ativar'}
                >
                  {campaign.status === 'STARTED' ? <i className="fas fa-pause" aria-hidden /> : <i className="fas fa-play" aria-hidden />}
                  {campaign.status === 'STARTED' ? 'Pausar' : 'Ativar'}
                </button>
              )}

              {campaign.status === 'DRAFT' && (
                <button
                  type="button"
                  onClick={async () => {
                    if (!campaign) return;
                    const triggerNode = nodes.find(n => n.data.nodeType === 'trigger');
                    if (!triggerNode) {
                      toast.error('Adicione um nó Início ao fluxo antes de ativar');
                      return;
                    }
                    const triggerConfig = triggerNode.data.config;
                    if (!triggerConfig?.connections?.length) {
                      toast.error('Configure pelo menos uma conexão WhatsApp no Início');
                      return;
                    }
                    const isOutbound = campaign?.type === 'OUTBOUND';
                    if (isOutbound) {
                      if (!triggerConfig?.categories?.length) {
                        toast.error('Configure pelo menos uma categoria de contatos no Início');
                        return;
                      }
                    }
                    let scheduledDateTime = null;
                    if (campaign?.type === 'OUTBOUND' && triggerConfig?.scheduleType === 'scheduled') {
                      if (!triggerConfig?.scheduledDate || !triggerConfig?.scheduledTime) {
                        toast.error('Por favor, defina a data e hora do agendamento no Início');
                        return;
                      }
                      scheduledDateTime = new Date(`${triggerConfig.scheduledDate}T${triggerConfig.scheduledTime}:00`);
                    }
                    const message = scheduledDateTime
                      ? `Deseja ativar a ${campaign?.type === 'INBOUND' ? 'bot' : 'campanha'} "${campaign.name}"?\nEla será iniciada em: ${scheduledDateTime.toLocaleString('pt-BR')}`
                      : `Deseja ativar a ${campaign?.type === 'INBOUND' ? 'bot' : 'campanha'} "${campaign.name}"? ${campaign?.type === 'INBOUND' ? 'O bot começará a escutar mensagens imediatamente.' : 'Ela começará a processar mensagens imediatamente.'}`;
                    if (!confirm(message)) return;
                    try {
                      await handleSave();
                      await interactiveCampaignApi.publishCampaign(campaign.id, scheduledDateTime || undefined);
                      if (scheduledDateTime) {
                        toast.success(`${campaign?.type === 'INBOUND' ? 'Bot' : 'Campanha'} agendado para ${scheduledDateTime.toLocaleString('pt-BR')}!`);
                      } else {
                        toast.success(`${campaign.type === 'INBOUND' ? 'Bot' : 'Campanha'} ativado com sucesso!`);
                      }
                      await loadCampaign(campaign.id);
                    } catch (error: any) {
                      toast.error(`Erro ao ativar: ${error.message}`);
                    }
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 transition-colors"
                  title="Ativar"
                >
                  <i className="fas fa-play" aria-hidden />
                  Ativar
                </button>
              )}
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setShowPreview(true)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <i className="fas fa-eye" aria-hidden />
                Preview
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              >
                {saving ? <i className="fas fa-spinner fa-spin" aria-hidden /> : <i className="fas fa-cloud-arrow-down" aria-hidden />}
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
              <input ref={fileInputRef} type="file" accept=".json,application/json" className="hidden" onChange={handleImportFlow} />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center justify-center w-10 h-10 rounded-xl border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                title="Importar fluxo"
              >
                <i className="fas fa-file-arrow-up" aria-hidden />
              </button>
              <button
                type="button"
                onClick={handleExportFlow}
                className="inline-flex items-center justify-center w-10 h-10 rounded-xl border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                title="Exportar fluxo"
              >
                <i className="fas fa-file-arrow-down" aria-hidden />
              </button>
            </div>
          </div>

          {/* Flow Builder (canvas) */}
          <main className="flex-1 min-h-0 relative bg-gray-50 dark:bg-gray-800/30">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onInit={setReactFlowInstance}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            defaultViewport={{ x: 0, y: 0, zoom: 0.15 }}
            fitView
            fitViewOptions={{ padding: 0.2 }}
          >
            <MiniMap
              nodeColor={(node) => {
                const nodeType = node.data.nodeType;
                // Verificar primeiro nos nós de integração
                if (INTEGRATION_NODES_CONFIG[nodeType as keyof typeof INTEGRATION_NODES_CONFIG]) {
                  return INTEGRATION_NODES_CONFIG[nodeType as keyof typeof INTEGRATION_NODES_CONFIG].color;
                }
                // Depois verificar nos nós padrão
                return NODE_TYPES_CONFIG[nodeType as keyof typeof NODE_TYPES_CONFIG]?.color || '#233e4f';
              }}
              zoomable
              pannable
            />
            <Controls />
            <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
          </ReactFlow>
        </main>
        </div>

        {/* Config Sidebar */}
        {selectedNode && (
          <NodeConfigSidebar
            node={selectedNode}
            nodes={nodes}
            edges={edges}
            connections={connections}
            categories={categories}
            withoutCategoryCount={withoutCategoryCount ?? undefined}
            campaignType={campaign?.type}
            onClose={() => setSelectedNode(null)}
            onSave={handleNodeConfigSave}
          />
        )}

        {showPreview && (
          <FlowPreviewModal
            nodes={nodes}
            edges={edges}
            campaignType={campaign?.type}
            onClose={() => setShowPreview(false)}
          />
        )}
      </div>
    </div>
  );
}

export function FlowBuilderPage() {
  return (
    <ReactFlowProvider>
      <FlowBuilderPageInner />
    </ReactFlowProvider>
  );
}
